<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>IABF {{ adminFestivalYear }} Costume Orders</h2>
            <v-btn to="/iabf/admin/tutu-order" class="ml-2">
              Tutu Order
            </v-btn>
            <v-btn to="/iabf/admin/borrowed-tutus" class="ml-2">
              Borrowed Tutus
            </v-btn>
            <!-- 2026-only corps pieces (Becoming / Garland Waltz) -->
            <v-btn v-if="show2026CorpsTools" to="/iabf/admin/becoming-order" class="ml-2">
              Becoming Order
            </v-btn>
            <v-btn class="ml-2" @click="exportCsv">
              Export Pieces CSV
            </v-btn>
            <v-btn v-if="show2026CorpsTools" class="ml-2" @click="exportGarlandCsv">
              Garland Cast CSV
            </v-btn>

            <!-- Financial Summary Panel -->
            <div v-if="isFinanceAdmin" class="finance-panel mt-4 mb-2">
              <div class="finance-h">Financial Summary <span class="muted small">(2026 borrowed-collection investment)</span></div>
              <div class="finance-grid">
                <div class="finance-stat">
                  <span class="finance-num">{{ fmtMoney(dancerInvoiced) }}</span>
                  <span class="finance-label">Invoiced to dancers</span>
                </div>
                <div class="finance-stat">
                  <span class="finance-num">{{ fmtMoney(dancerPaidCents) }}</span>
                  <span class="finance-label">Paid by dancers</span>
                </div>
                <div class="finance-stat">
                  <span class="finance-num finance-warn" v-if="dancerOutstanding > 0">{{ fmtMoney(dancerOutstanding) }}</span>
                  <span class="finance-num" v-else>$0</span>
                  <span class="finance-label">Still owed</span>
                </div>
                <div class="finance-stat finance-input">
                  <span class="finance-label finance-label-top">IABF total spend on costumes</span>
                  <input type="number" v-model.number="iabfTotalSpent" min="0" step="0.01" placeholder="0" class="finance-input-field" @change="saveCostumeConfig" />
                </div>
                <div class="finance-stat finance-net" v-if="iabfTotalSpent > 0">
                  <span class="finance-num" :class="iabfNet >= 0 ? 'finance-pos' : 'finance-neg'">{{ iabfNet >= 0 ? fmtMoney(iabfNet) : '−' + fmtMoney(Math.abs(iabfNet)) }}</span>
                  <span class="finance-label">IABF net investment in borrowed collection</span>
                  <span class="muted xsmall">(IABF spend − dancers paid)</span>
                </div>
              </div>
            </div>

            <!-- Order Summary Panel -->
            <v-expansion-panels flat class="summary-panels mt-4 mb-2">
              <v-expansion-panel>
                <v-expansion-panel-header class="summary-panel-header">
                  Order Summary
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Warnings -->
                  <div v-if="orderSummary.warnings.noColorAssigned.length || orderSummary.warnings.noMeasurements.length || orderSummary.warnings.sharedNotInAlice.length || orderSummary.warnings.sharedWrongRole.length" class="summary-warnings">
                    <div v-if="orderSummary.warnings.noColorAssigned.length" class="warning-item">
                      ⚠ <strong>No color assigned</strong> (buying/borrowing WoH tutu): {{ orderSummary.warnings.noColorAssigned.join(', ') }}
                    </div>
                    <div v-if="orderSummary.warnings.noMeasurements.length" class="warning-item">
                      ⚠ <strong>No measurements</strong> (buying/borrowing a tutu): {{ orderSummary.warnings.noMeasurements.join(', ') }}
                    </div>
                    <div v-if="orderSummary.warnings.sharedNotInAlice.length" class="warning-item">
                      ⚠ <strong>"Shared w/ Alice" but not in Alice</strong>: {{ orderSummary.warnings.sharedNotInAlice.join(', ') }}
                    </div>
                    <div v-if="orderSummary.warnings.sharedWrongRole.length" class="warning-item">
                      ⚠ <strong>"Shared w/ Alice" but Alice role is Pointe</strong> (pancake tutu, not romantic — can't share): {{ orderSummary.warnings.sharedWrongRole.join(', ') }}
                    </div>
                  </div>

                  <div class="summary-grid">
                    <!-- Color Pancake Tutus -->
                    <div class="summary-card">
                      <div class="summary-card-title">Color Pancake Tutus <span class="summary-card-sub">(Waltz of the Hours)</span></div>
                      <div class="summary-row"><span>Buy</span><strong>{{ orderSummary.woh.buy }}</strong></div>
                      <div class="summary-row"><span>Borrow</span><strong>{{ orderSummary.woh.borrow }}</strong></div>
                      <div class="summary-row"><span>Bringing own</span><strong>{{ orderSummary.woh.own }}</strong></div>
                      <div class="summary-divider"></div>
                      <div class="summary-card-sub-title">By color</div>
                      <div v-if="Object.keys(orderSummary.colorCounts).length === 0" class="summary-empty">No colors assigned yet</div>
                      <div v-for="(counts, color) in orderSummary.colorCounts" :key="color" class="summary-row color-row">
                        <span>{{ color }}</span>
                        <span class="color-breakdown">
                          <span v-if="counts.buy">{{ counts.buy }} buy</span>
                          <span v-if="counts.borrow">{{ counts.borrow }} borrow</span>
                          <span v-if="counts.own">{{ counts.own }} own</span>
                        </span>
                      </div>
                      <div v-if="orderSummary.warnings.noColorAssigned.length" class="summary-warning-inline">
                        {{ orderSummary.warnings.noColorAssigned.length }} unassigned
                      </div>
                    </div>

                    <!-- White Pancake Tutus -->
                    <div class="summary-card">
                      <div class="summary-card-title">White Pancake Tutus <span class="summary-card-sub">(Alice — Pointe role)</span></div>
                      <div class="summary-row"><span>Buy</span><strong>{{ orderSummary.aiwPancake.buy }}</strong></div>
                      <div class="summary-row"><span>Borrow</span><strong>{{ orderSummary.aiwPancake.borrow }}</strong></div>
                      <div class="summary-row"><span>Bringing own</span><strong>{{ orderSummary.aiwPancake.own }}</strong></div>
                    </div>

                    <!-- White Romantic Tutus -->
                    <div class="summary-card">
                      <div class="summary-card-title">White Romantic Tutus</div>
                      <div class="summary-card-sub-title">Alice — Flat Ladies</div>
                      <div class="summary-row"><span>Buy</span><strong>{{ orderSummary.aiwRomantic.buy }}</strong></div>
                      <div class="summary-row"><span>Borrow</span><strong>{{ orderSummary.aiwRomantic.borrow }}</strong></div>
                      <div class="summary-row"><span>Bringing own</span><strong>{{ orderSummary.aiwRomantic.own }}</strong></div>
                      <div class="summary-divider"></div>
                      <div class="summary-card-sub-title">Garland Waltz — Ladies</div>
                      <div class="summary-row"><span>Buy</span><strong>{{ orderSummary.gw.buy }}</strong></div>
                      <div class="summary-row"><span>Borrow</span><strong>{{ orderSummary.gw.borrow }}</strong></div>
                      <div class="summary-row"><span>Bringing own</span><strong>{{ orderSummary.gw.own }}</strong></div>
                      <div class="summary-row"><span>Shared w/ Alice</span><strong>{{ orderSummary.gw.shared }}</strong></div>
                      <div class="summary-divider"></div>
                      <div class="summary-row summary-total"><span>Total buy</span><strong>{{ orderSummary.aiwRomantic.buy + orderSummary.gw.buy }}</strong></div>
                      <div class="summary-row summary-total"><span>Total borrow</span><strong>{{ orderSummary.aiwRomantic.borrow + orderSummary.gw.borrow }}</strong></div>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <vue-good-table
              :columns="columns"
              :rows="rows"
              :line-numbers="true"
              :search-options="{
                enabled: true,
                placeholder: 'Search dancers',
              }"
              :sort-options="{
                enabled: true,
                initialSortBy: { field: 'costumeStatus', type: 'desc' }
              }"
            >
              <div slot="emptystate">
                No dancers match your search!
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'firstName'">
                  {{ props.row.firstName }}
                  <v-btn
                    x-small
                    icon
                    @click="openEdit(props.row)"
                    title="Edit costume selections"
                  >
                    <v-icon x-small>mdi-pencil</v-icon>
                  </v-btn>
                </span>
                <span v-else-if="props.column.field === 'costumeStatus'">
                  <span :style="statusStyle(props.row.costumeStatus)">
                    {{ props.row.costumeStatus }}
                  </span>
                  <v-btn
                    v-if="props.row.costumeStatus === 'Yes - Unpaid'"
                    x-small
                    color="success"
                    class="ml-2"
                    @click="markPaid(props.row)"
                    :loading="props.row.paidLoading"
                  >
                    Mark Paid
                  </v-btn>
                  <v-btn
                    v-if="props.row.costumeStatus === 'Yes - Unpaid'"
                    x-small
                    color="blue-grey"
                    dark
                    class="ml-1"
                    :href="props.row.stripeLink || undefined"
                    :disabled="!props.row.stripeLink"
                    target="_blank"
                  >
                    Stripe
                  </v-btn>
                  <v-btn
                    v-if="props.row.costumeStatus === 'Yes - Paid'"
                    x-small
                    color="grey"
                    class="ml-2"
                    outlined
                    @click="markUnpaid(props.row)"
                    :loading="props.row.paidLoading"
                  >
                    Mark Unpaid
                  </v-btn>
                </span>
                <span v-else-if="props.column.field === 'paymentDisplay'">
                  <span v-if="props.row.costumePaymentMethod === 'addToNext' && props.row.costumeStatus === 'Yes - Unpaid'">
                    <strong>${{ props.row.costumeTotalCost }}</strong> — Add to next
                    <v-btn
                      x-small
                      color="success"
                      class="ml-1"
                      @click="markPaid(props.row)"
                      :loading="props.row.paidLoading"
                    >
                      Done
                    </v-btn>
                  </span>
                  <span v-else>
                    {{ props.row.paymentDisplay }}
                  </span>
                </span>
                <span v-else-if="props.column.field === 'corpsStatus'">
                  <v-btn
                    x-small
                    :color="props.row.notInCorps ? 'grey' : 'success'"
                    :outlined="props.row.notInCorps"
                    @click="toggleCorps(props.row)"
                  >
                    {{ props.row.corpsStatus }}
                  </v-btn>
                </span>
                <span v-else-if="props.column.field === 'assignedWaltzColor'">
                  <span v-if="props.row.wohTutuChoice === 'own'" style="font-weight: bold;">
                    {{ props.row.ownTutuColor || '—' }}
                  </span>
                  <v-select
                    v-else-if="props.row.waltzColorOptions.length > 0"
                    v-model="props.row.assignedWaltzColor"
                    :items="[{ text: '(none)', value: '' }].concat(props.row.waltzColorOptions.map((c, i) => ({ text: (i+1) + '. ' + c + (waltzColorCounts[c] ? ' (' + waltzColorCounts[c] + ')' : ''), value: c })))"
                    dense
                    hide-details
                    @change="assignColor(props.row, 'assignedWaltzColor', props.row.assignedWaltzColor)"
                    :style="props.row.assignedWaltzColor ? { fontWeight: 'bold' } : {}"
                  ></v-select>
                </span>
                <span v-else-if="props.column.field === 'becomingShipped'">
                  <label v-if="props.row.becomingNeedsShipping" class="ship-toggle">
                    <input
                      type="checkbox"
                      :checked="!!props.row.becomingShippedAt"
                      :disabled="savingShippedId === props.row.id"
                      @change="onToggleBecomingShipped(props.row, $event.target.checked)"
                    />
                    <span v-if="props.row.becomingShippedAt" class="ship-date">{{ formatBecomingShippedAt(props.row.becomingShippedAt) }}</span>
                  </label>
                  <span v-else class="muted small">—</span>
                </span>
                <span v-else-if="props.column.field === 'assignedGarlandColor'">
                  <v-select
                    v-if="props.row.garlandColorOptions.length > 0"
                    v-model="props.row.assignedGarlandColor"
                    :items="[{ text: '(none)', value: '' }].concat(props.row.garlandColorOptions.map((c, i) => ({ text: (i+1) + '. ' + c + (garlandColorCounts[c] ? ' (' + garlandColorCounts[c] + ')' : ''), value: c })))"
                    dense
                    hide-details
                    @change="assignColor(props.row, 'assignedGarlandColor', props.row.assignedGarlandColor)"
                    :style="props.row.assignedGarlandColor ? { fontWeight: 'bold' } : {}"
                  ></v-select>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>

            <!-- Edit Costume Dialog -->
            <v-dialog v-model="editDialog" max-width="580" persistent>
              <v-card v-if="editDancer">
                <v-card-title>Edit Costume — {{ editDancer.firstName }} {{ editDancer.lastName }}</v-card-title>
                <v-card-text style="max-height: 72vh; overflow-y: auto;">

                  <!-- Waltz of Hours -->
                  <div class="edit-section">
                    <div class="d-flex align-center mb-1">
                      <h4 class="mr-auto">Waltz of the Hours</h4>
                      <v-switch v-model="editData.woh.participating" dense hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                    <div v-if="editData.woh.participating">
                      <v-select
                        v-model="editData.woh.tutuChoice"
                        :items="[{ text: 'Buy ($275)', value: 'buy' }, { text: 'Borrow ($0)', value: 'borrow' }, { text: 'Bring own', value: 'own' }]"
                        label="Pancake Tutu" dense
                      ></v-select>
                      <v-text-field v-if="editData.woh.tutuChoice === 'own'" v-model="editData.woh.tutuColor" label="Tutu Color" dense></v-text-field>
                      <v-layout>
                        <v-flex xs4 class="pr-1"><v-text-field v-model="editData.woh.colorPref1" label="Color Pref 1" dense></v-text-field></v-flex>
                        <v-flex xs4 class="px-1"><v-text-field v-model="editData.woh.colorPref2" label="Color Pref 2" dense></v-text-field></v-flex>
                        <v-flex xs4 class="pl-1"><v-text-field v-model="editData.woh.colorPref3" label="Color Pref 3" dense></v-text-field></v-flex>
                      </v-layout>
                    </div>
                  </div>

                  <!-- Alice in Wonderland -->
                  <div class="edit-section">
                    <div class="d-flex align-center mb-1">
                      <h4 class="mr-auto">Alice in Wonderland</h4>
                      <v-switch v-model="editData.aiw.participating" dense hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                    <div v-if="editData.aiw.participating">
                      <v-select
                        v-model="editData.aiw.role"
                        :items="['Pointe', 'Flat Ladies', 'Men & Jokers', 'Queen of Hearts']"
                        label="Role" dense
                      ></v-select>
                      <v-select
                        v-if="editData.aiw.role && editData.aiw.role !== 'Men & Jokers' && editData.aiw.role !== 'Queen of Hearts'"
                        v-model="editData.aiw.tutuChoice"
                        :items="[{ text: 'Buy', value: 'buy' }, { text: 'Borrow ($0)', value: 'borrow' }, { text: 'Bring own', value: 'own' }]"
                        label="White Tutu" dense
                      ></v-select>
                    </div>
                  </div>

                  <!-- Becoming -->
                  <div class="edit-section">
                    <div class="d-flex align-center mb-1">
                      <h4 class="mr-auto">Becoming</h4>
                      <v-switch v-model="editData.bec.participating" dense hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                    <div v-if="editData.bec.participating">
                      <v-select
                        v-model="editData.bec.topChoice"
                        :items="[{ text: 'Crop top ($50)', value: 'crop' }, { text: 'Crop top + shipping ($65)', value: 'crop_ship' }, { text: 'Full coverage leotard', value: 'leotard' }]"
                        label="Top" dense
                      ></v-select>
                      <v-text-field v-if="editData.bec.topChoice === 'crop' || editData.bec.topChoice === 'crop_ship'" v-model="editData.bec.cropTopSize" label="Crop Top Size" dense></v-text-field>
                      <v-select
                        v-model="editData.bec.skirtChoice"
                        :items="[{ text: 'Buy ($65)', value: 'buy' }, { text: 'Borrow ($0)', value: 'borrow' }]"
                        label="Blue Skirt" dense
                      ></v-select>
                      <v-text-field v-model="editData.bec.skirtSize" label="Skirt Size" dense></v-text-field>
                    </div>
                  </div>

                  <!-- Garland Waltz -->
                  <div class="edit-section">
                    <div class="d-flex align-center mb-1">
                      <h4 class="mr-auto">Garland Waltz</h4>
                      <v-switch v-model="editData.gw.participating" dense hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                    <div v-if="editData.gw.participating">
                      <v-select
                        v-model="editData.gw.role"
                        :items="['Ladies', 'Gentlemen']"
                        label="Role" dense
                      ></v-select>
                      <div v-if="editData.gw.role === 'Ladies'">
                        <v-checkbox v-model="editData.gw.tutuSharedWithAlice" label="White tutu shared with Alice" dense hide-details class="mt-1"></v-checkbox>
                        <v-select
                          v-if="!editData.gw.tutuSharedWithAlice"
                          v-model="editData.gw.tutuChoice"
                          :items="[{ text: 'Buy ($75)', value: 'buy' }, { text: 'Borrow ($0)', value: 'borrow' }, { text: 'Bring own', value: 'own' }]"
                          label="White Romantic Tutu" dense class="mt-2"
                        ></v-select>
                        <v-layout class="mt-1">
                          <v-flex xs4 class="pr-1"><v-text-field v-model="editData.gw.colorPref1" label="Color Pref 1" dense></v-text-field></v-flex>
                          <v-flex xs4 class="px-1"><v-text-field v-model="editData.gw.colorPref2" label="Color Pref 2" dense></v-text-field></v-flex>
                          <v-flex xs4 class="pl-1"><v-text-field v-model="editData.gw.colorPref3" label="Color Pref 3" dense></v-text-field></v-flex>
                        </v-layout>
                      </div>
                    </div>
                  </div>

                  <!-- Measurements -->
                  <div class="edit-section">
                    <div class="d-flex align-center mb-2">
                      <h4 class="mr-auto">Measurements</h4>
                      <v-select
                        v-model="editData.measurements.unit"
                        :items="[{ text: 'Inches', value: 'inches' }, { text: 'cm', value: 'cm' }]"
                        dense hide-details style="max-width: 100px;"
                      ></v-select>
                    </div>
                    <v-layout wrap>
                      <v-flex xs6 sm4 class="pr-2"><v-text-field v-model.number="editData.measurements.bust" label="Bust" type="number" step="0.1" dense></v-text-field></v-flex>
                      <v-flex xs6 sm4 class="pr-2"><v-text-field v-model.number="editData.measurements.waist" label="Waist" type="number" step="0.1" dense></v-text-field></v-flex>
                      <v-flex xs6 sm4 class="pr-2"><v-text-field v-model.number="editData.measurements.hip" label="Hip" type="number" step="0.1" dense></v-text-field></v-flex>
                      <v-flex xs6 sm4 class="pr-2"><v-text-field v-model.number="editData.measurements.halfGirth" label="½ Girth" type="number" step="0.1" dense></v-text-field></v-flex>
                      <v-flex xs6 sm4 class="pr-2"><v-text-field v-model.number="editData.measurements.girth" label="Girth" type="number" step="0.1" dense></v-text-field></v-flex>
                      <v-flex xs6 sm4><v-text-field v-model.number="editData.measurements.height" label="Height" type="number" step="0.1" dense></v-text-field></v-flex>
                    </v-layout>
                  </div>

                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="editDialog = false">Cancel</v-btn>
                  <v-btn color="primary" @click="saveEdit" :loading="editSaving">Save Changes</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <h3 class="mt-5">Add to Next Payment</h3>
            <div v-if="addToNextItems.length === 0" class="add-to-next-empty">
              No pending "add to next" costume charges.
            </div>
            <v-simple-table v-else class="add-to-next-table mt-2 mb-6">
              <thead>
                <tr>
                  <th>Dancer</th>
                  <th>Costume Total</th>
                  <th>Payment #</th>
                  <th>Current Amount</th>
                  <th>New Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in addToNextItems" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td>${{ item.costumeTotalCost }}</td>
                  <td>Payment {{ item.paymentNum }}</td>
                  <td>${{ item.currentAmount }}</td>
                  <td><strong>${{ item.currentAmount + item.costumeTotalCost }}</strong></td>
                  <td>
                    <v-btn
                      small
                      color="primary"
                      :loading="addToNextLoading[item.id]"
                      @click="addToPayment(item)"
                    >
                      Add ${{ item.costumeTotalCost }} to Payment {{ item.paymentNum }}
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <h3 class="mt-5">Dancers who haven't ordered yet:</h3>
            <p>{{ notOrderedEmails }}</p>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style scoped>
.finance-panel { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 14px 18px; }
.finance-h { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 16px; margin-bottom: 10px; }
.finance-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; align-items: stretch; }
.finance-stat { background: #faf8f3; border-radius: 6px; padding: 10px 14px; display: flex; flex-direction: column; gap: 2px; }
.finance-num { font-family: Quattrocento, Georgia, serif; font-size: 24px; font-weight: 700; color: #1a1a1a; line-height: 1.1; }
.finance-label { font-size: 12px; color: #888; }
.finance-label-top { margin-bottom: 4px; }
.finance-warn { color: #c43a54; }
.finance-pos { color: #2e7d32; }
.finance-neg { color: #c43a54; }
.finance-input-field { width: 100%; padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; font-family: Quattrocento, Georgia, serif; font-size: 20px; font-weight: 700; }
.finance-net { background: #eef6f8; border: 1px solid #c9e0e6; }
.muted { color: #888; }
.muted.small { font-style: italic; font-weight: 400; font-size: 13px; }
.xsmall { font-size: 11px; color: #aaa; }
</style>

<style>
  h2 {
    font-family: Quattrocento;
    font-weight: 400;
    font-size: 39px;
    letter-spacing: -.02em;
    line-height: 1em;
  }

  h3 {
    font-family: ltc-bodoni-175;
    font-size: 22px;
  }

  p, li {
    font-family: Fanwood Text;
    font-weight: 400;
    font-size: 19px;
    letter-spacing: .01em;
    line-height: 1.7em;
    color: rgba(40,26,23,.6);
  }

  .vgt-table, v-btn {
    font-family: Fanwood Text;
  }

  .vgt-table thead {
    font-size: 1.2em !important;
  }

  /* Order Summary Panel */
  .summary-panels {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  .summary-panel-header {
    font-family: ltc-bodoni-175;
    font-size: 18px;
    font-weight: 600;
  }

  .summary-warnings {
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 20px;
  }

  .warning-item {
    font-family: 'Fanwood Text', Georgia, serif;
    font-size: 14px;
    color: #5d4037;
    margin-bottom: 6px;
    line-height: 1.5;
  }

  .warning-item:last-child { margin-bottom: 0; }

  .summary-grid {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .summary-card {
    flex: 1;
    min-width: 200px;
    border: 1px solid #e8e5e1;
    border-radius: 4px;
    padding: 16px;
    background: #faf9f7;
  }

  .summary-card-title {
    font-family: ltc-bodoni-175;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #1a1a1a;
  }

  .summary-card-sub {
    font-size: 13px;
    font-weight: 400;
    color: #888;
  }

  .summary-card-sub-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: #888;
    margin: 8px 0 4px;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-family: 'Fanwood Text', Georgia, serif;
    font-size: 15px;
    padding: 3px 0;
    color: #444;
  }

  .summary-row strong { font-size: 16px; color: #1a1a1a; }

  .summary-total { border-top: 1px solid #e8e5e1; margin-top: 2px; padding-top: 6px; }
  .summary-total strong { font-size: 17px; }

  .summary-divider { border-top: 1px solid #e8e5e1; margin: 8px 0; }

  .summary-empty { font-size: 13px; color: #bbb; font-style: italic; }

  .color-row span:first-child { font-weight: 600; }

  .color-breakdown {
    display: flex;
    gap: 8px;
    font-size: 13px;
    color: #666;
  }

  .summary-warning-inline {
    font-size: 13px;
    color: #e65100;
    font-weight: 600;
    margin-top: 6px;
  }

  .add-to-next-empty {
    font-family: 'Fanwood Text', Georgia, serif;
    font-size: 15px;
    color: #888;
    padding: 8px 0 24px;
  }

  .add-to-next-table th {
    font-family: ltc-bodoni-175;
    font-size: 13px;
  }

  .edit-section {
    border-bottom: 1px solid #eee;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .edit-section:last-child {
    border-bottom: none;
  }

  .edit-section h4 {
    font-family: ltc-bodoni-175;
    font-size: 18px;
    margin-bottom: 8px;
  }

  .ship-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }

  .ship-date {
    font-family: 'Fanwood Text', Georgia, serif;
    font-size: 12px;
    color: #2e7d32;
    font-weight: 700;
    white-space: nowrap;
  }
</style>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { hasCorpsVideoAdmin } from '@/config/iabf-event'

import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'
import { db } from '@/firebase'
import firebase from 'firebase/app'

export default {
  mixins: [iabfAdminMixin],
    components: {
      VueGoodTable
    },
    data () {
      return {
        columns: [
          { label: 'First Name', field: 'firstName' },
          { label: 'Last Name', field: 'lastName' },
          { label: 'Email', field: 'email' },
          { label: 'Corps', field: 'corpsStatus' },
          { label: 'Submitted', field: 'submittedAt', type: 'date', dateInputFormat: "yyyy-MM-dd'T'HH:mm:ss", dateOutputFormat: 'M/d h:mm a' },
          { label: 'Costume Ordered', field: 'costumeStatus' },
          { label: 'Payment', field: 'paymentDisplay' },
          { label: 'Color Tutu', field: 'colorTutuDisplay' },
          { label: 'Waltz Color', field: 'assignedWaltzColor' },
          { label: 'White Pancake (Alice)', field: 'whitePancakeTutuDisplay' },
          { label: 'White Romantic (Alice)', field: 'whiteRomanticAliceTutuDisplay' },
          { label: 'White Romantic (Garland)', field: 'whiteRomanticGarlandTutuDisplay' },
          { label: 'Blue Skirt', field: 'blueSkirtDisplay' },
          { label: 'Blue Top', field: 'blueTopDisplay' },
          { label: 'Crop Top Size', field: 'cropTopSize' },
          { label: 'Skirt Size', field: 'skirtSize' },
          { label: 'Becoming Shipped?', field: 'becomingShipped' },
          { label: 'Garland Color', field: 'assignedGarlandColor' },
          { label: 'Cummerbund (in)', field: 'cummerbundMeasure' }
        ],
        rows: [],
        addToNextItems: [],
        addToNextLoading: {},
        savingShippedId: null,
        editDialog: false,
        editDancer: null,
        editData: { woh: {}, aiw: {}, bec: {}, gw: {} },
        editSaving: false,
        iabfTotalSpent: 0,
        costumeConfigLoaded: false
      }
    },
    async created () {
      // Load the admin-entered IABF costume spend so we can show the net
      // investment in the borrowed-tutu collection.
      try {
        const { db } = await import('@/firebase')
        const cfg = await db.collection(this.IABF.config).doc('costumes').get()
        if (cfg.exists) {
          this.iabfTotalSpent = Number((cfg.data() || {}).iabfTotalSpent) || 0
        }
        this.costumeConfigLoaded = true
      } catch (e) { /* ignore — admin can still type a value */ }
      this.$store.dispatch('loadIABFDancers').then(() => {
        this.$store.getters.loadedIABFDancers.forEach((dancer) => {
          this.rows.push(this.buildRow(dancer))
          if (dancer.costumePaymentMethod === 'addToNext' && dancer.costumeOrderCompleted && !dancer.costumeOrderPaid) {
            const paymentNum = this.nextUnpaidPayment(dancer)
            if (paymentNum) {
              this.addToNextItems.push({
                id: dancer.id,
                name: dancer.firstName + ' ' + dancer.lastName,
                costumeTotalCost: dancer.costumeTotalCost || 0,
                paymentNum,
                currentAmount: dancer[`payment${paymentNum}Due`] || 0
              })
            }
          }
        })
      })
    },
    computed: {
      isFinanceAdmin () {
        return this.$store.getters.isFinanceAdmin
      },
      /** Becoming / Garland Waltz tools existed only for 2026 corps pieces. */
      show2026CorpsTools () {
        return hasCorpsVideoAdmin(this.adminFestivalYear)
      },
      // $ values invoiced + paid across all dancer costume orders.
      // costumeStatus values seen in the codebase: 'Yes - Unpaid',
      // 'Yes - Paid', '' (no order). Treat anything that isn't blank or
      // 'No' as an active invoice.
      dancerInvoiced () {
        return this.rows.reduce((s, r) => {
          const status = r.costumeStatus || ''
          if (!status || status === 'No') return s
          return s + (Number(r.costumeTotalCost) || 0)
        }, 0)
      },
      dancerPaidCents () {
        return this.rows.reduce((s, r) => {
          if ((r.costumeStatus || '') === 'Yes - Paid') return s + (Number(r.costumeTotalCost) || 0)
          return s
        }, 0)
      },
      dancerOutstanding () {
        return Math.max(0, this.dancerInvoiced - this.dancerPaidCents)
      },
      iabfNet () {
        return (Number(this.iabfTotalSpent) || 0) - this.dancerPaidCents
      },
      orderSummary () {
        const woh = { buy: 0, borrow: 0, own: 0 }
        const aiwPancake = { buy: 0, borrow: 0, own: 0 }
        const aiwRomantic = { buy: 0, borrow: 0, own: 0 }
        const gw = { buy: 0, borrow: 0, own: 0, shared: 0 }
        const colorCounts = {}
        const warnings = { noColorAssigned: [], noMeasurements: [], sharedNotInAlice: [], sharedWrongRole: [] }

        this.rows.forEach(r => {
          const w = r.rawWoh || {}
          const a = r.rawAiw || {}
          const g = r.rawGw || {}
          const m = r.rawMeasurements || {}
          const name = r.firstName + ' ' + r.lastName

          // Color pancake (WoH)
          if (w.participating) {
            if (w.tutuChoice === 'buy') woh.buy++
            else if (w.tutuChoice === 'borrow') woh.borrow++
            else if (w.tutuChoice === 'own') woh.own++

            if ((w.tutuChoice === 'buy' || w.tutuChoice === 'borrow') && !r.assignedWaltzColor) {
              warnings.noColorAssigned.push(name)
            }

            const color = w.tutuChoice === 'own' ? w.tutuColor : r.assignedWaltzColor
            if (color) {
              if (!colorCounts[color]) colorCounts[color] = { buy: 0, borrow: 0, own: 0 }
              if (w.tutuChoice === 'buy') colorCounts[color].buy++
              else if (w.tutuChoice === 'borrow') colorCounts[color].borrow++
              else if (w.tutuChoice === 'own') colorCounts[color].own++
            }
          }

          // White pancake (Alice - Pointe)
          if (a.participating && a.role === 'Pointe') {
            if (a.tutuChoice === 'buy') aiwPancake.buy++
            else if (a.tutuChoice === 'borrow') aiwPancake.borrow++
            else if (a.tutuChoice === 'own') aiwPancake.own++
          }

          // White romantic (Alice - Flat Ladies)
          if (a.participating && a.role === 'Flat Ladies') {
            if (a.tutuChoice === 'buy') aiwRomantic.buy++
            else if (a.tutuChoice === 'borrow') aiwRomantic.borrow++
            else if (a.tutuChoice === 'own') aiwRomantic.own++
          }

          // White romantic (Garland Waltz)
          if (g.participating && g.role === 'Ladies') {
            if (g.tutuSharedWithAlice) gw.shared++
            else if (g.tutuChoice === 'buy') gw.buy++
            else if (g.tutuChoice === 'borrow') gw.borrow++
            else if (g.tutuChoice === 'own') gw.own++
          }

          // Warning: buying/borrowing any tutu but no measurements
          const needsMeasurements =
            (w.participating && (w.tutuChoice === 'buy' || w.tutuChoice === 'borrow')) ||
            (a.participating && a.role === 'Pointe' && (a.tutuChoice === 'buy' || a.tutuChoice === 'borrow')) ||
            (a.participating && a.role === 'Flat Ladies' && (a.tutuChoice === 'buy' || a.tutuChoice === 'borrow')) ||
            (g.participating && g.role === 'Ladies' && !g.tutuSharedWithAlice && (g.tutuChoice === 'buy' || g.tutuChoice === 'borrow'))
          if (needsMeasurements && !m.bust && !m.waist && !m.hip) {
            warnings.noMeasurements.push(name)
          }

          // Warning: shared w/ Alice but not in Alice
          if (g.participating && g.role === 'Ladies' && g.tutuSharedWithAlice && !a.participating) {
            warnings.sharedNotInAlice.push(name)
          }

          // Warning: shared w/ Alice but Alice role is Pointe (pancake, not romantic — can't share)
          if (g.participating && g.role === 'Ladies' && g.tutuSharedWithAlice && a.participating && a.role === 'Pointe') {
            warnings.sharedWrongRole.push(name)
          }
        })

        return { woh, aiwPancake, aiwRomantic, gw, colorCounts, warnings }
      },
      waltzColorCounts () {
        const counts = {}
        this.rows.forEach(r => {
          if (r.wohTutuChoice === 'own' && r.ownTutuColor) {
            counts[r.ownTutuColor] = (counts[r.ownTutuColor] || 0) + 1
          } else if (r.assignedWaltzColor) {
            counts[r.assignedWaltzColor] = (counts[r.assignedWaltzColor] || 0) + 1
          }
        })
        return counts
      },
      garlandColorCounts () {
        const counts = {}
        this.rows.forEach(r => {
          if (r.assignedGarlandColor) {
            counts[r.assignedGarlandColor] = (counts[r.assignedGarlandColor] || 0) + 1
          }
        })
        return counts
      },
      notOrderedEmails () {
        return this.rows
          .filter(r => r.costumeStatus === 'No' && !r.notInCorps)
          .map(r => r.email)
          .join(', ')
      }
    },
    methods: {
      fmtMoney (n) {
        // Round to whole dollars when the cents are clean — otherwise show
        // .XX. Suppresses floating-point junk like 1762.7299999999996.
        const v = Math.round((Number(n) || 0) * 100) / 100
        if (Math.abs(v - Math.round(v)) < 0.005) return '$' + Math.round(v).toLocaleString()
        return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      },
      async saveCostumeConfig () {
        try {
          const { db } = await import('@/firebase')
          await db.collection(this.IABF.config).doc('costumes').set({
            iabfTotalSpent: Number(this.iabfTotalSpent) || 0
          }, { merge: true })
        } catch (e) { console.error('Save costume config failed:', e) }
      },
      nextUnpaidPayment (dancer) {
        for (let i = 1; i <= 6; i++) {
          const due = dancer[`payment${i}Due`]
          const status = dancer[`payment${i}Status`]
          if (due != null && status !== 'Paid') return i
        }
        return null
      },
      async addToPayment (item) {
        this.$set(this.addToNextLoading, item.id, true)
        const newAmount = item.currentAmount + item.costumeTotalCost
        await Promise.all([
          this.$store.dispatch('assignCostumeColor', { id: item.id, field: `payment${item.paymentNum}Due`, color: newAmount }),
          this.$store.dispatch('setCostumeOrderPaid', { id: item.id, paid: true })
        ])
        const row = this.rows.find(r => r.id === item.id)
        if (row) {
          row.costumeStatus = 'Yes - Paid'
          row.paymentDisplay = 'Paid'
        }
        const idx = this.addToNextItems.findIndex(i => i.id === item.id)
        if (idx !== -1) this.addToNextItems.splice(idx, 1)
        this.$set(this.addToNextLoading, item.id, false)
      },
      toInches (value, unit) {
        if (!value) return ''
        const num = parseFloat(value)
        if (isNaN(num)) return ''
        if (unit === 'cm') return (num / 2.54).toFixed(1)
        return num.toFixed(1)
      },
      buildPaymentDisplay (costumeStatus, totalCost, paymentMethod) {
        if (costumeStatus === 'No') return ''
        if (costumeStatus === 'Yes - Paid') return 'Paid'
        if (totalCost === 0) return '$0'
        if (paymentMethod === 'addToNext') return '$' + totalCost + ' — Add to next'
        return '$' + totalCost
      },
      buildRow (dancer) {
        const ordered = dancer.costumeOrderCompleted || false
        const paid = dancer.costumeOrderPaid || false
        const totalCost = dancer.costumeTotalCost || 0
        let costumeStatus = 'No'
        if (ordered && (paid || totalCost === 0)) costumeStatus = 'Yes - Paid'
        else if (ordered) costumeStatus = 'Yes - Unpaid'

        const woh = dancer.waltzOfHours || {}
        const aiw = dancer.aliceInWonderland || {}
        const bec = dancer.becoming || {}
        const gw = dancer.garlandWaltz || {}
        const measurements = dancer.sharedMeasurements || {}
        const unit = measurements.unit || 'inches'

        // Color tutu display
        let colorTutuDisplay = ''
        if (woh.participating) {
          if (woh.tutuChoice === 'buy') colorTutuDisplay = 'Buy'
          else if (woh.tutuChoice === 'borrow') colorTutuDisplay = 'Borrow'
          else if (woh.tutuChoice === 'own') colorTutuDisplay = 'Bring' + (woh.tutuColor ? ' - ' + woh.tutuColor : '')
        }

        // White pancake tutu (Alice - Pointe role)
        let whitePancakeTutuDisplay = ''
        if (aiw.participating && aiw.role === 'Pointe') {
          if (aiw.tutuChoice === 'buy') whitePancakeTutuDisplay = 'Buy'
          else if (aiw.tutuChoice === 'borrow') whitePancakeTutuDisplay = 'Borrow'
          else if (aiw.tutuChoice === 'own') whitePancakeTutuDisplay = 'Bring'
        }

        // White romantic tutu (Alice - Flat Ladies role)
        let whiteRomanticAliceTutuDisplay = ''
        if (aiw.participating && aiw.role === 'Flat Ladies') {
          if (aiw.tutuChoice === 'buy') whiteRomanticAliceTutuDisplay = 'Buy'
          else if (aiw.tutuChoice === 'borrow') whiteRomanticAliceTutuDisplay = 'Borrow'
          else if (aiw.tutuChoice === 'own') whiteRomanticAliceTutuDisplay = 'Bring'
        }

        // White romantic tutu (Garland Waltz)
        let whiteRomanticGarlandTutuDisplay = ''
        if (gw.participating && gw.role === 'Ladies') {
          if (gw.tutuSharedWithAlice) whiteRomanticGarlandTutuDisplay = 'Shared w/ Alice'
          else if (gw.tutuChoice === 'buy') whiteRomanticGarlandTutuDisplay = 'Buy'
          else if (gw.tutuChoice === 'borrow') whiteRomanticGarlandTutuDisplay = 'Borrow'
          else if (gw.tutuChoice === 'own') whiteRomanticGarlandTutuDisplay = 'Bring'
        }

        // Blue skirt
        let blueSkirtDisplay = ''
        if (bec.participating) {
          if (bec.skirtChoice === 'buy') blueSkirtDisplay = 'Buy'
          else if (bec.skirtChoice === 'borrow') blueSkirtDisplay = 'Borrow'
        }

        // Blue top
        let blueTopDisplay = ''
        if (bec.participating) {
          if (bec.topChoice === 'crop') blueTopDisplay = 'Crop'
          else if (bec.topChoice === 'leotard') blueTopDisplay = 'Leotard'
        }

        // Cummerbund
        let cummerbundMeasure = ''
        if (gw.participating && gw.role === 'Gentlemen' && measurements.waist) {
          cummerbundMeasure = this.toInches(measurements.waist, unit)
        }

        // Waltz color options (dancer's 3 preferences)
        const waltzColorOptions = [woh.colorPref1, woh.colorPref2, woh.colorPref3].filter(c => c)

        // Garland color options (dancer's 3 preferences)
        const garlandColorOptions = [gw.colorPref1, gw.colorPref2, gw.colorPref3].filter(c => c)

        return {
          id: dancer.id,
          firstName: dancer.firstName,
          lastName: dancer.lastName,
          email: dancer.email,
          notInCorps: dancer.notInCorps || false,
          corpsStatus: dancer.notInCorps ? 'No' : 'Yes',
          submittedAt: dancer.costumeOrderSubmittedAt || '',
          costumeStatus: costumeStatus,
          costumeTotalCost: totalCost,
          costumePaymentMethod: dancer.costumePaymentMethod || '',
          paymentDisplay: this.buildPaymentDisplay(costumeStatus, totalCost, dancer.costumePaymentMethod),
          stripeLink: dancer.stripeLink || '',
          paidLoading: false,
          colorTutuDisplay: colorTutuDisplay,
          wohTutuChoice: woh.tutuChoice || '',
          waltzColorOptions: waltzColorOptions,
          ownTutuColor: (woh.participating && woh.tutuChoice === 'own' && woh.tutuColor) ? woh.tutuColor : '',
          assignedWaltzColor: dancer.assignedWaltzColor || '',
          whitePancakeTutuDisplay: whitePancakeTutuDisplay,
          whiteRomanticAliceTutuDisplay: whiteRomanticAliceTutuDisplay,
          whiteRomanticGarlandTutuDisplay: whiteRomanticGarlandTutuDisplay,
          blueSkirtDisplay: blueSkirtDisplay,
          blueTopDisplay: blueTopDisplay,
          cropTopSize: bec.cropTopSize || '',
          skirtSize: bec.skirtSize || '',
          becomingNeedsShipping: !!bec.participating && bec.topChoice === 'crop_ship',
          becomingShippedAt: bec.shippedAt || null,
          garlandColorOptions: garlandColorOptions,
          assignedGarlandColor: dancer.assignedGarlandColor || '',
          cummerbundMeasure: cummerbundMeasure,
          rawWoh: { ...woh },
          rawAiw: { ...aiw },
          rawBec: { ...bec },
          rawGw: { ...gw },
          rawLjh: { ...(dancer.leJazzHot || {}) },
          rawMeasurements: { ...measurements }
        }
      },
      markPaid (row) {
        row.paidLoading = true
        this.$store.dispatch('setCostumeOrderPaid', { id: row.id, paid: true }).then(() => {
          row.costumeStatus = 'Yes - Paid'
          row.paymentDisplay = 'Paid'
          row.paidLoading = false
        })
      },
      markUnpaid (row) {
        row.paidLoading = true
        this.$store.dispatch('setCostumeOrderPaid', { id: row.id, paid: false }).then(() => {
          row.costumeStatus = 'Yes - Unpaid'
          row.paymentDisplay = this.buildPaymentDisplay('Yes - Unpaid', row.costumeTotalCost, row.costumePaymentMethod)
          row.paidLoading = false
        })
      },
      toggleCorps (row) {
        const newVal = !row.notInCorps
        row.notInCorps = newVal
        row.corpsStatus = newVal ? 'No' : 'Yes'
        this.$store.dispatch('assignCostumeColor', { id: row.id, field: 'notInCorps', color: newVal })
      },
      formatBecomingShippedAt (ts) {
        if (!ts) return ''
        const d = ts.toDate ? ts.toDate() : (ts instanceof Date ? ts : new Date(ts))
        if (isNaN(d.getTime())) return ''
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      },
      async onToggleBecomingShipped (row, checked) {
        this.savingShippedId = row.id
        try {
          if (checked) {
            const ts = firebase.firestore.Timestamp.now()
            await db.collection(this.IABF.dancers).doc(row.id).update({
              'becoming.shippedAt': ts
            })
            this.$set(row, 'becomingShippedAt', ts)
            if (row.rawBec) this.$set(row.rawBec, 'shippedAt', ts)
          } else {
            await db.collection(this.IABF.dancers).doc(row.id).update({
              'becoming.shippedAt': firebase.firestore.FieldValue.delete()
            })
            this.$set(row, 'becomingShippedAt', null)
            if (row.rawBec) this.$set(row.rawBec, 'shippedAt', null)
          }
        } catch (e) {
          // eslint-disable-next-line no-alert
          window.alert('Failed to update shipped status: ' + (e.message || e))
        }
        this.savingShippedId = null
      },
      assignColor (row, field, color) {
        this.$store.dispatch('assignCostumeColor', { id: row.id, field: field, color: color })
      },
      openEdit (row) {
        this.editDancer = row
        this.editData = {
          woh: { participating: false, tutuChoice: '', tutuColor: '', colorPref1: '', colorPref2: '', colorPref3: '', ...row.rawWoh },
          aiw: { participating: false, role: '', tutuChoice: '', ...row.rawAiw },
          bec: { participating: false, topChoice: '', cropTopSize: '', skirtChoice: '', skirtSize: '', ...row.rawBec },
          gw: { participating: false, role: '', tutuChoice: '', tutuSharedWithAlice: false, colorPref1: '', colorPref2: '', colorPref3: '', ...row.rawGw },
          measurements: { unit: 'inches', bust: null, waist: null, hip: null, halfGirth: null, girth: null, height: null, ...row.rawMeasurements }
        }
        this.editDialog = true
      },
      calculateTotal (woh, aiw, bec, gw) {
        let total = 0
        if (woh.participating && woh.tutuChoice === 'buy') total += 275
        if (aiw.participating && aiw.role !== 'Men & Jokers' && aiw.role !== 'Queen of Hearts') {
          if (aiw.tutuChoice === 'buy') total += 75
        }
        if (bec.participating) {
          if (bec.topChoice === 'crop') total += 50
          if (bec.topChoice === 'crop_ship') total += 65
          if (bec.skirtChoice === 'buy') total += 65
        }
        if (gw.participating && gw.role === 'Ladies' && !gw.tutuSharedWithAlice) {
          if (gw.tutuChoice === 'buy') total += 75
        }
        return total
      },
      async saveEdit () {
        this.editSaving = true
        const id = this.editDancer.id
        const woh = this.editData.woh
        const aiw = this.editData.aiw
        const bec = this.editData.bec
        const gw = this.editData.gw

        const m = this.editData.measurements
        const newTotal = this.calculateTotal(woh, aiw, bec, gw)
        const oldTotal = this.editDancer.costumeTotalCost || 0
        const wasPaid = this.editDancer.costumeStatus === 'Yes - Paid'
        const totalChanged = newTotal !== oldTotal

        const saves = [
          this.$store.dispatch('assignCostumeColor', { id, field: 'waltzOfHours', color: { ...woh } }),
          this.$store.dispatch('assignCostumeColor', { id, field: 'aliceInWonderland', color: { ...aiw } }),
          this.$store.dispatch('assignCostumeColor', { id, field: 'becoming', color: { ...bec } }),
          this.$store.dispatch('assignCostumeColor', { id, field: 'garlandWaltz', color: { ...gw } }),
          this.$store.dispatch('assignCostumeColor', { id, field: 'sharedMeasurements', color: { ...m } })
        ]
        if (totalChanged) {
          saves.push(this.$store.dispatch('assignCostumeColor', { id, field: 'costumeTotalCost', color: newTotal }))
        }
        if (totalChanged && wasPaid) {
          saves.push(this.$store.dispatch('setCostumeOrderPaid', { id, paid: false }))
        }
        await Promise.all(saves)

        // Rebuild all display fields on the row
        const row = this.editDancer

        row.colorTutuDisplay = !woh.participating ? '' :
          woh.tutuChoice === 'buy' ? 'Buy' :
          woh.tutuChoice === 'borrow' ? 'Borrow' :
          woh.tutuChoice === 'own' ? 'Bring' + (woh.tutuColor ? ' - ' + woh.tutuColor : '') : ''

        const aiwChoice = c => c === 'buy' ? 'Buy' : c === 'borrow' ? 'Borrow' : c === 'own' ? 'Bring' : ''
        row.whitePancakeTutuDisplay = (aiw.participating && aiw.role === 'Pointe') ? aiwChoice(aiw.tutuChoice) : ''
        row.whiteRomanticAliceTutuDisplay = (aiw.participating && aiw.role === 'Flat Ladies') ? aiwChoice(aiw.tutuChoice) : ''
        if (gw.participating && gw.role === 'Ladies') {
          row.whiteRomanticGarlandTutuDisplay = gw.tutuSharedWithAlice ? 'Shared w/ Alice'
            : gw.tutuChoice === 'buy' ? 'Buy' : gw.tutuChoice === 'borrow' ? 'Borrow' : gw.tutuChoice === 'own' ? 'Bring' : ''
        } else {
          row.whiteRomanticGarlandTutuDisplay = ''
        }

        row.blueSkirtDisplay = !bec.participating ? '' : bec.skirtChoice === 'buy' ? 'Buy' : bec.skirtChoice === 'borrow' ? 'Borrow' : ''
        row.blueTopDisplay = !bec.participating ? '' : bec.topChoice === 'crop' || bec.topChoice === 'crop_ship' ? 'Crop' : bec.topChoice === 'leotard' ? 'Leotard' : ''
        row.cropTopSize = bec.participating ? (bec.cropTopSize || '') : ''
        row.skirtSize = bec.participating ? (bec.skirtSize || '') : ''

        row.wohTutuChoice = woh.tutuChoice || ''
        row.waltzColorOptions = woh.participating ? [woh.colorPref1, woh.colorPref2, woh.colorPref3].filter(c => c) : []
        row.garlandColorOptions = gw.participating ? [gw.colorPref1, gw.colorPref2, gw.colorPref3].filter(c => c) : []
        row.ownTutuColor = (woh.participating && woh.tutuChoice === 'own' && woh.tutuColor) ? woh.tutuColor : ''

        row.rawWoh = { ...woh }
        row.rawAiw = { ...aiw }
        row.rawBec = { ...bec }
        row.rawGw = { ...gw }
        row.rawMeasurements = { ...m }
        row.cummerbundMeasure = (gw.participating && gw.role === 'Gentlemen' && m.waist)
          ? this.toInches(m.waist, m.unit || 'inches') : row.cummerbundMeasure

        if (totalChanged) {
          row.costumeTotalCost = newTotal
          if (wasPaid) {
            row.costumeStatus = 'Yes - Unpaid'
            row.costumeOrderPaid = false
          }
          row.paymentDisplay = this.buildPaymentDisplay(row.costumeStatus, newTotal, row.costumePaymentMethod)
        }

        this.editSaving = false
        this.editDialog = false
      },
      exportCsv () {
        const headers = ['Name', 'Waltz of the Hours', 'Alice in Wonderland', 'Le Jazz Hot', 'Becoming', 'Garland Waltz']
        const csvRows = [headers.join(',')]
        this.rows.forEach(row => {
          const name = `${row.firstName} ${row.lastName}`
          const cols = [
            `"${name}"`,
            row.rawWoh && row.rawWoh.participating ? 'Y' : 'N',
            row.rawAiw && row.rawAiw.participating ? 'Y' : 'N',
            row.rawLjh && row.rawLjh.participating ? 'Y' : 'N',
            row.rawBec && row.rawBec.participating ? 'Y' : 'N',
            row.rawGw && row.rawGw.participating ? 'Y' : 'N'
          ]
          csvRows.push(cols.join(','))
        })
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'iabf-pieces.csv'
        a.click()
        URL.revokeObjectURL(url)
      },
      exportGarlandCsv () {
        const headers = ['Name', 'Role', 'Color Preference 1', 'Color Preference 2', 'Color Preference 3']
        const csvRows = [headers.join(',')]
        this.rows
          .filter(row => row.rawGw && row.rawGw.participating)
          .forEach(row => {
            const gw = row.rawGw
            const cols = [
              `"${row.firstName} ${row.lastName}"`,
              `"${gw.role || ''}"`,
              `"${gw.colorPref1 || ''}"`,
              `"${gw.colorPref2 || ''}"`,
              `"${gw.colorPref3 || ''}"`
            ]
            csvRows.push(cols.join(','))
          })
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'garland-waltz-cast.csv'
        a.click()
        URL.revokeObjectURL(url)
      },
      statusStyle (status) {
        if (status === 'Yes - Paid') return { color: 'green', fontWeight: 'bold' }
        if (status === 'Yes - Unpaid') return { color: 'orange', fontWeight: 'bold' }
        return { color: 'grey' }
      }
    }
  }
</script>
